import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AppDataService, OperatorService, VehicleBrandService } from 'app/services';
import {
    AddOperatorVehicleModel, ColorTypeSelection, DayOfWeekEnum, RiderVehicleType,
    RidesharingAppModel, VehicleBrandModel, VehicleModel, RiderVehicleTypeEnum
} from 'app/models';
import { AppComponent } from 'app/app.component';
import moment from 'moment';
import { Utils } from 'app/utils';
import { XpressDocumentTypeEnum } from '@shared/enums/xpress-document-type.enum';
import { XpToggleOption, XpToggleValue } from '@xp/toggle-buttons/xp-toggle-buttons.component';
import {
    fileBrowseHandlerForOperatorVehicle,
    flushStagedOperatorVehicleUploads,
    getOperatorVehicleUploadedFiles,
    hasOperatorVehicleMaxFilesUploaded,
    hasOperatorVehiclePdfUploaded,
    hasOperatorVehicleUploadedFiles,
    hasOperatorVehicleUploadsBoundToDifferentPlate,
    normalizeOperatorVehiclePlate,
    OPERATOR_VEHICLE_ACCEPTED_FILE_INPUT,
    OPERATOR_VEHICLE_MULTI_FILE_FIELDS,
    OPERATOR_VEHICLE_UPLOAD_FIELDS,
    OperatorVehicleUploadField,
    removeOperatorVehicleDocumentFileLocally,
    UploadedDocumentMeta,
    UploadedDocumentMetaMap
} from '../shared/operator-vehicle-document-upload';

enum VehicleStepProgressEnum {
    VehicleDetails = 1,
    VehiclePhotos = 2,
    VehicleOwnership = 3,
    TnvcDocuments = 4,
    BoundaryFee = 5
}

interface VehicleTypeInterface {
    VehicleType: RiderVehicleTypeEnum;
}

interface OperatorVehiclePhotoUploadSlot {
    label: string;
    field: OperatorVehicleUploadField;
}

@Component({
    selector: 'add-operator-vehicle',
    templateUrl: './add-operator-vehicle.component.html',
    styleUrls: ['./add-operator-vehicle.component.scss',
        '../../../../../../styles/menu-sidebar.scss',
        '../../../../../../styles/style-layout.scss'
    ],
})
export class AddOperatorVehicleComponent implements OnInit {
    public dayOptions: XpToggleOption[] = [
        { label: 'Sun', value: DayOfWeekEnum.Sunday },
        { label: 'Mon', value: DayOfWeekEnum.Monday },
        { label: 'Tue', value: DayOfWeekEnum.Tuesday },
        { label: 'Wed', value: DayOfWeekEnum.Wednesday },
        { label: 'Thu', value: DayOfWeekEnum.Thursday },
        { label: 'Fri', value: DayOfWeekEnum.Friday },
        { label: 'Sat', value: DayOfWeekEnum.Saturday }
    ];

    public toggleDay(day: XpToggleValue): void {
        const days = [...(this.addOperatorVehicleModel.BoundaryAvailableDays ?? [])];
        const index = days.indexOf(day as DayOfWeekEnum);
        if (index >= 0) {
            days.splice(index, 1);
        } else {
            days.push(day as DayOfWeekEnum);
        }
        this.addOperatorVehicleModel.BoundaryAvailableDays = days;
    }

    constructor(private messageService: MessageService, public appData: AppDataService,
        public app: AppComponent, private operatorService: OperatorService,
        private vehicleBrandService: VehicleBrandService) { }

    @Input()
    public menuPermissionActive!: string;
    @Output() changedOpenAddOperatorVehicleEmit = new EventEmitter<boolean | { added: true }>();
    changedOpenAddOperatorVehicle(value: boolean | { added: true }) {
        this.changedOpenAddOperatorVehicleEmit.emit(value);
    }

    public addOperatorVehicleModel: AddOperatorVehicleModel = new AddOperatorVehicleModel();
    public vehicleBrands!: any;
    public vehicleModels!: VehicleModel[];
    public showValidationError: boolean = false;
    public vehicleStepProgressEnum = VehicleStepProgressEnum;
    public vehicleStepProgressActive: VehicleStepProgressEnum = VehicleStepProgressEnum.VehicleDetails;
    public colorTypeList = ColorTypeSelection.ColorTypeList;
    public operatorVehicleTypeList = RiderVehicleType.OperatorVehicleTypeList;
    public ridesharingAppModelList = RidesharingAppModel.RidesharingAppModelList;
    public minDate: Date = new Date();

    public vehiclePhotoUploadSlots: OperatorVehiclePhotoUploadSlot[] = [
        { label: 'Front', field: 'VehicleFrontImageUrl' },
        { label: 'Rear', field: 'VehicleBackImageUrl' },
        { label: 'Right Side', field: 'VehicleRightSideImageUrl' },
        { label: 'Left Side', field: 'VehicleLeftSideImageUrl' }
    ];

    private uploadedDocumentMeta: UploadedDocumentMetaMap = {};
    public readonly acceptedVehicleDocTypes = OPERATOR_VEHICLE_ACCEPTED_FILE_INPUT;
    public readonly multiFileDocFields = OPERATOR_VEHICLE_MULTI_FILE_FIELDS;

    timeStart: Date = new Date(new Date().setHours(0, 0, 0, 0));
    timeEnd: Date = new Date(new Date().setHours(23, 59, 0, 0));

    get timeStartStr(): string {
        const h = this.timeStart.getHours().toString().padStart(2, '0');
        const m = this.timeStart.getMinutes().toString().padStart(2, '0');
        return `${h}:${m}`;
    }

    set timeStartStr(val: string) {
        if (!val) { return; }
        const [h, m] = val.split(':').map(Number);
        this.timeStart = new Date(new Date().setHours(h, m, 0, 0));
    }

    get timeEndStr(): string {
        const h = this.timeEnd.getHours().toString().padStart(2, '0');
        const m = this.timeEnd.getMinutes().toString().padStart(2, '0');
        return `${h}:${m}`;
    }

    set timeEndStr(val: string) {
        if (!val) { return; }
        const [h, m] = val.split(':').map(Number);
        this.timeEnd = new Date(new Date().setHours(h, m, 0, 0));
    }

    public chooseYear(normalizedYear: Date, dp: any) {
        this.addOperatorVehicleModel.VehicleYearStr = normalizedYear;
        this.setBuildYear();
        dp.close();
    }

    ngOnInit(): void {
        this.getVehicleBrands();
    }

    private get xpressUserId(): number {
        return parseInt(this.appData.authData.UserId, 10) || 0;
    }

    isNoValuesInputted(): boolean {
        return (
            !this.addOperatorVehicleModel.VehicleBrandId &&
            !this.addOperatorVehicleModel.VehicleTypeId &&
            !this.addOperatorVehicleModel.VehicleModelId &&
            !this.addOperatorVehicleModel.VehicleYearStr &&
            !this.addOperatorVehicleModel.NumberPlate &&
            !this.addOperatorVehicleModel.VehicleColor &&
            !this.addOperatorVehicleModel.VehicleFrontImageUrlFile &&
            !this.addOperatorVehicleModel.LTOOfficialReceiptImageUrlFile &&
            !this.addOperatorVehicleModel.PaOrCpcCaseNumber &&
            !this.addOperatorVehicleModel.BoundaryFeeAmount
        );
    }

    setBuildYear() {
        this.addOperatorVehicleModel.VehicleYear = parseInt(moment(this.addOperatorVehicleModel.VehicleYearStr).format('YYYY'), 10);
    }

    public changeStepProgress(step: VehicleStepProgressEnum, status: boolean) {
        if (status) {
            this.vehicleStepProgressActive = step;
        }
    }

    /** Plate is frozen once any document is uploading or bound — uploads are keyed by DocumentNumber=plate. */
    public get isNumberPlateLocked(): boolean {
        return hasOperatorVehicleUploadedFiles(this.uploadedDocumentMeta)
            || OPERATOR_VEHICLE_UPLOAD_FIELDS.some(field =>
                this.isUploadFieldLoading(field) || this.hasUploadFieldUrl(field));
    }

    public getUploadedFiles(fieldName: OperatorVehicleUploadField): UploadedDocumentMeta[] {
        return getOperatorVehicleUploadedFiles(this.uploadedDocumentMeta, fieldName);
    }

    public hasPdfUploaded(fieldName: OperatorVehicleUploadField): boolean {
        return hasOperatorVehiclePdfUploaded(this.uploadedDocumentMeta, fieldName);
    }

    public canUploadMoreFiles(fieldName: OperatorVehicleUploadField): boolean {
        return !this.hasPdfUploaded(fieldName)
            && !hasOperatorVehicleMaxFilesUploaded(this.uploadedDocumentMeta, fieldName);
    }

    public isMultiFileDocField(fieldName: OperatorVehicleUploadField): boolean {
        return this.multiFileDocFields.has(fieldName);
    }

    public fileBrowseHandler(event: any, fieldName: OperatorVehicleUploadField) {
        const plate = normalizeOperatorVehiclePlate(this.addOperatorVehicleModel.NumberPlate);
        if (!plate) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Enter the plate number in Vehicle Details before uploading documents.'
            });
            return;
        }

        if (hasOperatorVehicleUploadsBoundToDifferentPlate(this.uploadedDocumentMeta, plate)) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Existing uploads were saved under a different plate. Delete them, correct the plate, then re-upload.'
            });
            return;
        }

        fileBrowseHandlerForOperatorVehicle(
            event,
            fieldName,
            this.messageService,
            this.addOperatorVehicleModel,
            this.uploadedDocumentMeta,
            plate
        );
    }

    public isUploadFieldLoading(field: OperatorVehicleUploadField): boolean {
        const model = this.addOperatorVehicleModel as unknown as Record<string, unknown>;
        return !!model[field + 'IsLoading'];
    }

    public hasUploadFieldUrl(field: OperatorVehicleUploadField): boolean {
        return !!this.getUploadFieldUrl(field);
    }

    public getUploadFieldUrl(field: OperatorVehicleUploadField): string {
        const model = this.addOperatorVehicleModel as unknown as Record<string, unknown>;
        const url = model[field];
        return typeof url === 'string' ? url : '';
    }

    public deleteUpload(fieldName: OperatorVehicleUploadField, fileId?: number) {
        const files = this.getUploadedFiles(fieldName);
        const targetId = fileId ?? files[0]?.id;
        if (targetId == null) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Unable to delete: document id is missing.'
            });
            return;
        }

        // Files are staged until final Submit — remove locally only.
        const removed = removeOperatorVehicleDocumentFileLocally(
            this.addOperatorVehicleModel,
            this.uploadedDocumentMeta,
            fieldName,
            targetId
        );
        if (!removed) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Unable to delete: document id is missing.'
            });
        }
    }

    private getVehicleBrands() {
        const dto: VehicleTypeInterface = {
            VehicleType: this.addOperatorVehicleModel.VehicleTypeId,
        };

        this.vehicleBrandService.getVehicleBrands(dto, this.messageService)
            .then(data => {
                if (!data) { return; }
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.vehicleBrands = VehicleBrandModel.mapToVehicleBrandModels(data.Items);
            });
    }

    private getVehicleModels() {
        const dto = {
            VehicleBrandId: this.addOperatorVehicleModel.VehicleBrandId,
            VehicleType: this.addOperatorVehicleModel.VehicleTypeId
        };

        this.vehicleBrandService.getVehicleModels(dto, this.messageService)
            .then(data => {
                if (!data) { return; }
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.vehicleModels = VehicleModel.mapToVehicleModels(data.Items);
            });
    }

    public onChangeVehicleMakeEvent(event: any) {
        this.addOperatorVehicleModel.VehicleColor = null;
        this.addOperatorVehicleModel.VehicleModelId = null;
        if (this.addOperatorVehicleModel.VehicleBrandId && this.addOperatorVehicleModel.VehicleTypeId) {
            this.getVehicleModels();
        }
    }

    public onChangeVehicleModelEvent(event: any) {
        if (this.addOperatorVehicleModel.VehicleBrandId) {
            this.addOperatorVehicleModel.VehicleColor = null;
        }
    }

    public onChangeVehicleTypeEvent(event: any) {
        this.onChangeVehicleMakeEvent(event);
        this.vehicleBrands = null;
        this.addOperatorVehicleModel.VehicleColor = null;
        if (this.addOperatorVehicleModel.VehicleTypeId) {
            this.getVehicleBrands();
        }
    }

    public checkFieldsByStepProgress(): boolean {
        switch (this.vehicleStepProgressActive) {
            case VehicleStepProgressEnum.VehicleDetails:
                return this.addOperatorVehicleModel.IsVehicleDetailsFieldsFilled();
            case VehicleStepProgressEnum.VehiclePhotos:
                return this.addOperatorVehicleModel.IsVehiclePhotoFieldsFilled();
            case VehicleStepProgressEnum.VehicleOwnership:
                return this.addOperatorVehicleModel.IsVehicleOwnershipFieldsFilled();
            case VehicleStepProgressEnum.TnvcDocuments:
                return this.addOperatorVehicleModel.IsTnvcFieldsFilled();
            case VehicleStepProgressEnum.BoundaryFee:
                return this.addOperatorVehicleModel.IsBoundaryFieldsFilled();
            default:
                return false;
        }
    }

    private buildAddVehiclePayload() {
        const model = this.addOperatorVehicleModel;
        const vehicleModelId = typeof model.VehicleModelId === 'object' && model.VehicleModelId != null
            ? model.VehicleModelId.Id
            : model.VehicleModelId;

        return {
            VehicleTypeId: model.VehicleTypeId,
            VehicleBrandId: model.VehicleBrandId,
            VehicleModelId: vehicleModelId,
            VehicleYear: model.VehicleYear,
            VehicleColor: model.VehicleColor,
            NumberPlate: model.NumberPlate,
            RegistrationNumber: model.RegistrationNumber,
            OwnerFirstName: model.OwnerFirstName,
            OwnerLastName: model.OwnerLastName,
            OwnerEmail: model.OwnerEmail,
            OwnerPhone: model.OwnerPhone,
            IsOwner: model.IsOwner,
            IsRegisteredTNC: model.IsRegisteredTNC,
            TNCType: model.TNCType,
            PaOrCpcCaseNumber: model.PaOrCpcCaseNumber,
            BoundaryFeeAmount: model.BoundaryFeeAmount,
            BoundaryProcessingFee: model.BoundaryProcessingFee,
            BoundaryAvailableDays: model.BoundaryAvailableDays,
            BoundaryAvailableFromHours: model.BoundaryAvailableFromHours,
            BoundaryAvailableFromMins: model.BoundaryAvailableFromMins,
            BoundaryAvailableToHours: model.BoundaryAvailableToHours,
            BoundaryAvailableToMins: model.BoundaryAvailableToMins,
            CollectAtStart: model.CollectAtStart,
            TimeZoneOffset: model.TimeZoneOffset
        };
    }

    private saveDocumentExpiryDates(): Promise<any> {
        const expiryItems: any[] = [];
        const model = this.addOperatorVehicleModel;

        if (model.LTOOfficialReceiptExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration],
                ExpiryDate: moment(model.LTOOfficialReceiptExpireAt).format('YYYY-MM-DD')
            });
        }
        if (model.CertificatePublicConvenienceExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Cpc],
                ExpiryDate: moment(model.CertificatePublicConvenienceExpireAt).format('YYYY-MM-DD')
            });
        }
        if (model.ProvisionalAuthorityExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Pa],
                ExpiryDate: moment(model.ProvisionalAuthorityExpireAt).format('YYYY-MM-DD')
            });
        }

        if (expiryItems.length === 0) {
            return Promise.resolve({ Success: true });
        }

        const plate = normalizeOperatorVehiclePlate(this.addOperatorVehicleModel.NumberPlate);
        if (!plate) {
            return Promise.resolve({
                Success: false,
                ErrorMessage: 'Enter the plate number in Vehicle Details before saving document expiry dates.'
            });
        }

        if (hasOperatorVehicleUploadsBoundToDifferentPlate(this.uploadedDocumentMeta, plate)) {
            return Promise.resolve({
                Success: false,
                ErrorMessage: 'Uploaded documents do not match the current plate number. Re-upload documents before saving.'
            });
        }

        return this.operatorService.updateDocumentExpiryDates({
            XpressUserId: this.xpressUserId > 0 ? this.xpressUserId : null,
            DocumentNumber: plate,
            Items: expiryItems
        }, this.messageService);
    }

    public clickNext() {
        if (this.checkFieldsByStepProgress()) {
            if (this.vehicleStepProgressActive === VehicleStepProgressEnum.BoundaryFee) {
                this.addOperatorVehicleModel.BoundaryAvailableFromHours = moment(this.timeStart).hour();
                this.addOperatorVehicleModel.BoundaryAvailableFromMins = moment(this.timeStart).minute();
                this.addOperatorVehicleModel.BoundaryAvailableToHours = moment(this.timeEnd).hour();
                this.addOperatorVehicleModel.BoundaryAvailableToMins = moment(this.timeEnd).minute();
                if (this.addOperatorVehicleModel.IsRequiredFieldsFilled()) {
                    this.addVehicle();
                }
                return;
            }

            this.vehicleStepProgressActive++;
            this.showValidationError = false;
        } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
            this.showValidationError = true;
        }
    }

    private addVehicle() {
        const plate = normalizeOperatorVehiclePlate(this.addOperatorVehicleModel.NumberPlate);
        if (!plate || hasOperatorVehicleUploadsBoundToDifferentPlate(this.uploadedDocumentMeta, plate)) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Uploaded documents do not match the current plate number. Re-upload documents before saving.'
            });
            return;
        }

        this.addOperatorVehicleModel.TimeZoneOffset = Utils.getTimezoneOffset();

        // Browse only stages files locally — upload to the server on final Submit.
        flushStagedOperatorVehicleUploads(
            this.operatorService,
            this.messageService,
            this.addOperatorVehicleModel,
            this.uploadedDocumentMeta,
            this.xpressUserId,
            plate
        )
            .then(uploadResult => {
                if (!uploadResult.Success) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: uploadResult.ErrorMessage || 'Failed to upload documents.'
                    });
                    return null;
                }

                return this.operatorService.addVehicle(this.buildAddVehiclePayload(), this.messageService);
            })
            .then(data => {
                if (!data) {
                    return null;
                }
                if (!data?.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage || 'Failed to add vehicle.' });
                    return Promise.reject(new Error(data?.ErrorMessage || 'Failed to add vehicle.'));
                }

                return this.saveDocumentExpiryDates();
            })
            .then(expiryResult => {
                if (!expiryResult) {
                    return;
                }
                if (expiryResult && !expiryResult.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: expiryResult.ErrorMessage });
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Vehicle successfully added',
                        detail: 'Your vehicle is pending review. You can find it under Inactive Vehicles.'
                    });
                }

                // New vehicles are Pending + inactive — open Inactive tab after create
                this.changedOpenAddOperatorVehicle({ added: true });
                this.showValidationError = false;
            })
            .catch(err => {
                this.showValidationError = false;
                if (err?.message && !String(err.message).includes('Failed to add vehicle')) {
                    throw err;
                }
            });
    }

    public backToOperatorVehicles() {
        this.changedOpenAddOperatorVehicle(false);
    }

    protected readonly Utils = Utils;
}
